import { AIGeneratedField } from '@/redux/slices/formAIGenerationSlice'
import { nanoid } from '@reduxjs/toolkit'
import { BuiltFieldAttributes, FieldBuilderParams } from './baseFieldBuilder'

export default function examSettingBuilder({
  rawField,
  attributes,
  scene,
}: FieldBuilderParams): Partial<BuiltFieldAttributes> | undefined {
  if (scene !== 'exam') return

  return buildAdditionalAttrsForScorableField(rawField, attributes)
}

const SCORABLE_SELECTABLE_FIELD_TYPES = ['RadioButton', 'CheckBox', 'DropDown']
export const SCORABLE_CUSTOMIZED_TYPES_MAP: Record<string, string[]> = {
  RadioButton: ['SingleSelect', 'TrueOrFalse'],
  CheckBox: ['MultiSelect'],
  DropDown: ['DropDownSelect'],
  TextField: ['FillInBlank'],
  TextArea: ['ShortAnswer'],
  NumberField: ['FillInNumber'],
}

function buildAdditionalAttrsForScorableField(
  rawField: AIGeneratedField,
  additionalAttrs: BuiltFieldAttributes
): BuiltFieldAttributes | undefined {
  const { examSetting, customizedType } = rawField
  if (!examSetting) return

  if (SCORABLE_SELECTABLE_FIELD_TYPES.includes(rawField.type)) {
    if (!additionalAttrs.choices?.length) return

    const correctAnswers =
      examSetting.answer
        ?.map(
          answer =>
            additionalAttrs.choices![parseAnswerToChoiceIndex(answer)]?.value ??
            additionalAttrs.choices!.find(choice => choice.name === answer)?.value
        )
        .filter((v): v is string => !!v) ?? []
    if (!correctAnswers[0]) correctAnswers.push(additionalAttrs.choices[0]!.value)

    const isMultipleSelect = rawField.type === 'CheckBox'

    return {
      customizedType:
        customizedType && SCORABLE_CUSTOMIZED_TYPES_MAP[rawField.type]?.includes(customizedType)
          ? customizedType
          : SCORABLE_CUSTOMIZED_TYPES_MAP[rawField.type]?.[0],
      extraConfigurableSettings: {
        type: 'AnswerBank',
        answerSettingMode: 'absolute',
        answers: [
          {
            apiCode: nanoid(4),
            score: examSetting.score ?? 0,
            correctAnswer: isMultipleSelect ? correctAnswers : correctAnswers[0],
          },
        ],
        answerExplanation: examSetting.explanation ?? undefined,
        enableAnswerExplanation: !!examSetting.explanation,
      },
    } as BuiltFieldAttributes
  }

  if (rawField.type === 'TextField' || rawField.type === 'TextArea') {
    return {
      customizedType: rawField.type === 'TextField' ? 'FillInBlank' : 'ShortAnswer',
      extraConfigurableSettings: {
        type: 'AnswerBank',
        answerSettingMode: 'relative',
        answers: [
          {
            apiCode: nanoid(4),
            score: examSetting.score ?? 0,
            correctAnswer: examSetting.answer?.[0] || '请输入正确答案',
          },
        ],
        answerExplanation: examSetting.explanation ?? undefined,
        enableAnswerExplanation: !!examSetting.explanation,
      },
    } as BuiltFieldAttributes
  }

  if (rawField.type === 'NumberField') {
    const correctAnswer = parseFloat(examSetting.answer?.[0] ?? '0') || 0

    return {
      customizedType: 'FillInNumber',
      extraConfigurableSettings: {
        type: 'AnswerBank',
        answerSettingMode: 'absolute',
        answers: [{ apiCode: nanoid(4), score: examSetting.score ?? 0, correctAnswer }],
        answerExplanation: examSetting.explanation ?? undefined,
        enableAnswerExplanation: !!examSetting.explanation,
      },
    } as BuiltFieldAttributes
  }
}

const ANSWER_CHAR_CODE_START = 'A'.charCodeAt(0)
const ANSWER_CHAR_CODE_END = 'Z'.charCodeAt(0)

function parseAnswerToChoiceIndex(answer: string): number {
  if (!answer) return -1

  const charCode = answer.toUpperCase().charCodeAt(0)
  if (charCode < ANSWER_CHAR_CODE_START || charCode > ANSWER_CHAR_CODE_END) return -1
  return charCode - ANSWER_CHAR_CODE_START
}
